/**
 * Lokale UTKAST for reisefilmen (eier-ønske: «lag draft på videoer man har
 * arbeidet med, uten å måtte re-velge alle filene»). Vi lagrer innstillingene
 * PLUSS en KOPI av de valgte klippene/bildene i IndexedDB på ENHETEN — aldri
 * opplastet (personvern). Grunnen til at vi må kopiere bytene (ikke bare
 * referere de originale galleri-filene): en Android galleri-`File` er backet av
 * en `content://`-URI med MIDLERTIDIG lesetilgang som utløper mellom økter — en
 * ren referanse ville vært død når utkastet åpnes senere (samme rotårsak som
 * «videoer vises ikke»-feilen). Chromium disk-backer store Blob-er, så minnet
 * holder. Egen DB fra `savedAudioStore` (unngår versjons-koordinering).
 */

const DB_NAME = 'fricamp-replay-drafts';
const STORE = 'drafts';
const DB_VERSION = 1;

/** Ett kopiert medieinnslag i et utkast (bytes ligger i `blob`). */
export interface DraftMedia {
  scope: 'trip' | 'stop' | 'audio';
  spotId?: string;          // kun scope 'stop'
  kind: 'photo' | 'video';
  name: string;
  type: string;
  atMs?: number;            // kun scope 'trip' — kronologisk plassering
  blob: Blob;
}

export interface ReplayDraft {
  id: string;
  name: string;
  updatedAt: number;
  /** Serialiserbare innstillinger (periode, stil, tempo, orientering, musikk, ekskluderte stopp …). */
  settings: Record<string, unknown>;
  media: DraftMedia[];
}

/** Metadata uten de tunge blob-ene — til liste-visning. */
export interface ReplayDraftSummary {
  id: string;
  name: string;
  updatedAt: number;
  mediaCount: number;
  totalBytes: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB utilgjengelig')); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Lagrer/oppdaterer et utkast (put på id). */
export async function saveDraft(draft: ReplayDraft): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(draft);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

/** Alle utkast (kun sammendrag, uten blob-er), nyeste først. */
export async function listDrafts(): Promise<ReplayDraftSummary[]> {
  let db: IDBDatabase;
  try { db = await openDb(); } catch { return []; }
  try {
    const all = await new Promise<ReplayDraft[]>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve((req.result as ReplayDraft[]) || []);
      req.onerror = () => reject(req.error);
    });
    return all
      .map(d => ({
        id: d.id,
        name: d.name,
        updatedAt: d.updatedAt,
        mediaCount: d.media?.length ?? 0,
        totalBytes: (d.media ?? []).reduce((a, m) => a + (m.blob?.size ?? 0), 0),
      }))
      .sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  } finally {
    db.close();
  }
}

/** Henter ett fullt utkast (med blob-er) for gjenopptak. */
export async function getDraft(id: string): Promise<ReplayDraft | null> {
  let db: IDBDatabase;
  try { db = await openDb(); } catch { return null; }
  try {
    return await new Promise<ReplayDraft | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(id);
      req.onsuccess = () => resolve((req.result as ReplayDraft) || null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  } finally {
    db.close();
  }
}

/** Sletter et utkast. */
export async function deleteDraft(id: string): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

/** Deterministisk id (tidsstempel-basert, filnavn-trygt). */
export function makeDraftId(now: number): string {
  return `draft-${now}`;
}
